import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, Loader2, Users, Award, Star, XCircle, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { logActivity } from "@/lib/activityLogger";
import { StarRating } from "./StarRating";
import type { MusicianCheckIn, MusicianRosterInfo } from "./types";

interface Props {
  musicians: MusicianCheckIn[];
  roster: Record<string, MusicianRosterInfo>;
  inquiryId: string;
  eventCheckInId: string;
  eventDate: string;
  onUpdate: (musicianId: string, updates: Partial<MusicianCheckIn>) => void;
}

const STEPS = [
  { key: "arrived", label: "Arrived" },
  { key: "briefed", label: "Briefed" },
  { key: "gear", label: "Gear Verified" },
  { key: "soundcheck", label: "Sound Check" },
  { key: "perf_start", label: "Performance Started" },
  { key: "perf_end", label: "Performance Ended" },
  { key: "wrapped", label: "Wrapped Up" },
] as const;

function getCompletedStep(m: MusicianCheckIn): number {
  if (m.wrapped_up_at) return 7;
  if (m.performance_ended_at) return 6;
  if (m.performance_started_at) return 5;
  if (m.sound_check_at) return 4;
  if (m.equipment_verified) return 3;
  if (m.briefed_at) return 2;
  if (m.arrival_status === "arrived") return 1;
  return 0;
}

function getStepTimestamp(m: MusicianCheckIn, stepIndex: number): string | null {
  switch (stepIndex) {
    case 0: return m.actual_arrival;
    case 1: return m.briefed_at;
    case 2: return m.actual_arrival;
    case 3: return m.sound_check_at;
    case 4: return m.performance_started_at;
    case 5: return m.performance_ended_at;
    case 6: return m.wrapped_up_at;
    default: return null;
  }
}

function getExperienceBadge(count: number) {
  if (count === 0) return { label: "Rookie", color: "bg-amber-500/15 text-amber-700 border-amber-500/30" };
  if (count < 12) return { label: `${count} events`, color: "bg-blue-500/15 text-blue-700 border-blue-500/30" };
  if (count < 20) return { label: "12+ events", color: "bg-purple-500/15 text-purple-700 border-purple-500/30" };
  return { label: "Veteran (20+)", color: "bg-green-500/15 text-green-700 border-green-500/30" };
}

export function MusicianWorkflowCard({ musicians, roster, inquiryId, eventCheckInId, eventDate, onUpdate }: Props) {
  const [loading, setLoading] = useState<string | null>(null);
  const [showReview, setShowReview] = useState<string | null>(null);

  // Persist review drafts in sessionStorage
  const storageKey = "ops_musician_reviews";
  const loadReviews = useCallback(() => {
    try { return JSON.parse(sessionStorage.getItem(storageKey) || "{}"); } catch { return {}; }
  }, []);
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>(() => {
    const saved = loadReviews();
    const notes: Record<string, string> = {};
    musicians.forEach(m => { if (saved[m.id]?.notes) notes[m.id] = saved[m.id].notes; });
    return notes;
  });
  const [ratings, setRatings] = useState<Record<string, number>>(() => {
    const saved = loadReviews();
    const r: Record<string, number> = {};
    musicians.forEach(m => { if (saved[m.id]?.rating) r[m.id] = saved[m.id].rating; });
    return r;
  });

  const persistReview = useCallback((musicianId: string, notes: string, rating: number) => {
    const saved = loadReviews();
    saved[musicianId] = { notes, rating };
    sessionStorage.setItem(storageKey, JSON.stringify(saved));
  }, [loadReviews]);

  const clearReview = useCallback((musicianId: string) => {
    const saved = loadReviews();
    delete saved[musicianId];
    sessionStorage.setItem(storageKey, JSON.stringify(saved));
  }, [loadReviews]);

  const markNoShow = async (m: MusicianCheckIn) => {
    setLoading(m.id);
    try {
      const now = new Date().toISOString();
      const update = { arrival_status: "no_show", wrapped_up_at: now };
      onUpdate(m.id, update);
      const { error } = await supabase.from("musician_check_ins").update(update).eq("id", m.id);
      if (error) throw error;

      // Flag inquiry as stress point immediately
      await supabase.from("contact_submissions").update({ has_incident: true } as any).eq("id", inquiryId);
      await supabase.from("event_check_ins").update({ has_incident: true }).eq("id", eventCheckInId);

      // Log activity
      await logActivity({
        inquiryId,
        action: "incident_reported",
        description: `No-show: ${m.musician_name} (${m.instrument}) did not arrive`,
        metadata: { event_check_in_id: eventCheckInId, event_date: eventDate, musician_name: m.musician_name, instrument: m.instrument, type: "no_show" },
      });

      // Insert incident email into CRM thread
      const bodyHtml = `
        <div style="font-family:sans-serif;max-width:600px">
          <h2 style="color:#dc2626">⚠️ Musician No-Show Alert</h2>
          <p><strong>${m.musician_name}</strong> (${m.instrument}) did not arrive for the event on ${format(new Date(eventDate + "T00:00:00"), "MMM d, yyyy")}.</p>
          <p>The Regional Manager has flagged this as a no-show. Immediate attention may be required.</p>
          <hr/>
          <p style="font-size:11px;color:#666">Auto-generated by On-Site Ops at ${format(new Date(), "PPpp")}</p>
        </div>
      `;
      await supabase.from("inquiry_emails").insert({
        inquiry_id: inquiryId,
        recipient_email: "internal@theloststrings.com",
        subject: `⚠️ No-Show: ${m.musician_name} — ${format(new Date(eventDate + "T00:00:00"), "MMM d")}`,
        body_html: bodyHtml,
        status: "sent",
        template_used: "incident_report",
      });

      toast.success(`${m.musician_name}: marked as No Show — CRM notified`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(null);
    }
  };

  const advanceStep = async (m: MusicianCheckIn, nextStep: number) => {
    setLoading(m.id);
    try {
      let update: Record<string, any> = {};
      let localUpdate: Partial<MusicianCheckIn> = {};
      const now = new Date().toISOString();
      switch (nextStep) {
        case 1:
          update = { actual_arrival: now, arrival_status: "arrived" };
          localUpdate = { actual_arrival: now, arrival_status: "arrived" };
          break;
        case 2:
          update = { briefed_at: now };
          localUpdate = { briefed_at: now };
          break;
        case 3:
          update = { equipment_verified: true };
          localUpdate = { equipment_verified: true };
          break;
        case 4:
          update = { sound_check_at: now };
          localUpdate = { sound_check_at: now };
          break;
        case 5:
          update = { performance_started_at: now };
          localUpdate = { performance_started_at: now };
          break;
        case 6:
          update = { performance_ended_at: now };
          localUpdate = { performance_ended_at: now };
          break;
        case 7: {
          const rating = ratings[m.id] || 0;
          const notes = reviewNotes[m.id] || "";
          if (rating === 0) { toast.error("Please give a star rating before wrapping up"); setLoading(null); return; }
          if (notes.length < 20) { toast.error(`Review notes must be at least 20 characters (${notes.length}/20)`); setLoading(null); return; }
          update = { wrapped_up_at: now, performance_rating: rating, performance_review_notes: notes };
          localUpdate = { wrapped_up_at: now, performance_rating: rating, performance_review_notes: notes };
          break;
        }
      }
      onUpdate(m.id, localUpdate);
      const { error } = await supabase.from("musician_check_ins").update(update).eq("id", m.id);
      if (error) throw error;
      if (nextStep === 7) clearReview(m.id);
      toast.success(`${m.musician_name}: ${STEPS[nextStep - 1].label}`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Users className="h-4 w-4" /> Musicians ({musicians.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {musicians.length === 0 ? (
          <p className="text-xs text-muted-foreground">No musicians assigned</p>
        ) : musicians.map(m => {
          const isNoShow = m.arrival_status === "no_show";
          const completed = isNoShow ? 7 : getCompletedStep(m);
          const nextStep = completed < 7 ? completed + 1 : null;
          const info = m.musician_id ? roster[m.musician_id] : null;
          const expBadge = info ? getExperienceBadge(info.eventCount) : null;

          if (isNoShow) {
            return (
              <div key={m.id} className="p-3 border border-destructive/30 rounded-md space-y-2 bg-destructive/5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{m.musician_name}</p>
                    <p className="text-xs text-muted-foreground">{m.instrument}</p>
                  </div>
                  <Badge className="bg-destructive/15 text-destructive border-destructive/30 text-[10px]">
                    <XCircle className="h-3 w-3 mr-1" /> No Show
                  </Badge>
                </div>
              </div>
            );
          }

          return (
            <div key={m.id} className="p-3 border rounded-md space-y-2">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{m.musician_name}</p>
                  <p className="text-xs text-muted-foreground">{m.instrument}</p>
                  {info && (
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {info.tier && (
                        <Badge variant="outline" className="text-[9px] h-4 px-1.5 bg-primary/10 border-primary/30 text-primary">
                          <Star className="h-2.5 w-2.5 mr-0.5" />{info.tier}
                        </Badge>
                      )}
                      {info.composite_score != null && (
                        <Badge variant="outline" className="text-[9px] h-4 px-1.5">
                          Score: {info.composite_score}
                        </Badge>
                      )}
                      {expBadge && (
                        <Badge variant="outline" className={`text-[9px] h-4 px-1.5 ${expBadge.color}`}>
                          <Award className="h-2.5 w-2.5 mr-0.5" />{expBadge.label}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                {m.phone && (
                  <a href={`tel:${m.phone}`} className="text-xs text-primary underline">
                    {m.phone}
                  </a>
                )}
              </div>

              {/* Step progress */}
              <div className="space-y-1">
                {STEPS.map((step, i) => {
                  const stepNum = i + 1;
                  const isDone = completed >= stepNum;
                  const isNext = nextStep === stepNum;
                  const ts = isDone ? getStepTimestamp(m, i) : null;

                  return (
                    <div key={step.key} className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isDone ? "bg-green-500 text-white" : isNext ? "border-2 border-primary" : "border border-muted-foreground/30"
                      }`}>
                        {isDone && <CheckCircle className="h-3 w-3" />}
                      </div>
                      <span className={`text-[11px] flex-1 ${isDone ? "text-green-700" : isNext ? "font-medium" : "text-muted-foreground"}`}>
                        {step.label}
                      </span>
                      {isDone && ts && (
                        <span className="text-[9px] text-muted-foreground">
                          {format(new Date(ts), "h:mm a")}
                        </span>
                      )}
                      {isNext && stepNum !== 7 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[10px] h-6 px-2"
                          disabled={loading === m.id}
                          onClick={() => advanceStep(m, stepNum)}
                        >
                          {loading === m.id ? <Loader2 className="h-3 w-3 animate-spin" /> : `Mark ${step.label}`}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Wrap-up review */}
              {nextStep === 7 && (
                <div className="space-y-2 pt-1">
                  {showReview === m.id ? (
                    <>
                      <div>
                        <label className="text-[10px] font-medium">Performance Rating *</label>
                        <StarRating value={ratings[m.id] || 0} onChange={v => { setRatings(prev => ({ ...prev, [m.id]: v })); persistReview(m.id, reviewNotes[m.id] || "", v); }} />
                      </div>
                      <div>
                        <Textarea
                          placeholder="Performance notes (min 20 characters) *"
                          rows={2}
                          value={reviewNotes[m.id] || ""}
                          onChange={e => { setReviewNotes(prev => ({ ...prev, [m.id]: e.target.value })); persistReview(m.id, e.target.value, ratings[m.id] || 0); }}
                          className="text-xs"
                        />
                        <p className={`text-[9px] mt-0.5 ${(reviewNotes[m.id]?.length || 0) >= 20 ? "text-green-600" : "text-amber-600"}`}>
                          {reviewNotes[m.id]?.length || 0}/20 characters
                        </p>
                      </div>
                      <Button
                        size="sm"
                        className="w-full text-xs h-7"
                        disabled={loading === m.id || (ratings[m.id] || 0) === 0 || (reviewNotes[m.id]?.length || 0) < 20}
                        onClick={() => advanceStep(m, 7)}
                      >
                        {loading === m.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Complete Wrap-Up"}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-xs h-7"
                      onClick={() => setShowReview(m.id)}
                    >
                      Wrap Up & Review
                    </Button>
                  )}
                </div>
              )}

              {/* No Show button (only before arrival) */}
              {completed === 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-xs h-7 border-destructive/50 text-destructive hover:bg-destructive/10"
                  disabled={loading === m.id}
                  onClick={() => markNoShow(m)}
                >
                  {loading === m.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><AlertTriangle className="h-3 w-3 mr-1" /> Mark No Show</>}
                </Button>
              )}

              {/* Completed badge */}
              {completed === 7 && (
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-500/15 text-green-700 border-green-500/30 text-[10px]">
                    <CheckCircle className="h-3 w-3 mr-1" /> Wrapped
                  </Badge>
                  {m.performance_rating && <StarRating value={m.performance_rating} readonly size="sm" />}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
